import { createSimpleContext } from "@opencode-ai/ui/context"
import { createEffect, createMemo, createResource } from "solid-js"
import { createStore } from "solid-js/store"
import { getWorkspaceSettings, listCompanies, listPeriods, listProjects } from "@/cowork/api"
import { activeCompanyID, setActiveCompanyID } from "@/cowork/local-auth"
import { splitWorkspacePath } from "@/cowork/workspace-route"
import { rememberCoworkResponseLanguage } from "@/cowork/response-language"

const ALL_ENTITIES = "ALL"
const ALL_PERIODS = "ALL"
const ENTITY_STORAGE_KEY = "cowork.workspace.entityId"
const PROJECT_STORAGE_KEY = "cowork.workspace.projectId"
const PERIOD_STORAGE_KEY = "cowork.workspace.periodId"

function storedValue(key: string, fallback = "") {
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    return fallback
  }
}

function persistValue(key: string, value: string) {
  try {
    if (value) localStorage.setItem(key, value)
    else localStorage.removeItem(key)
  } catch {
    // The in-memory context still works when browser storage is unavailable.
  }
}

export const { use: useCoworkWorkspace, provider: CoworkWorkspaceProvider } = createSimpleContext({
  name: "CoworkWorkspace",
  init: () => {
    // A scoped URL must not briefly restore another tab's saved company before routing resolves.
    const routed = window.location.pathname.startsWith("/") && splitWorkspacePath(window.location.pathname) !== undefined
    const [state, setState] = createStore({
      workspaceId: "workspace-current",
      entityId: routed || storedValue(ENTITY_STORAGE_KEY) === ALL_ENTITIES ? "" : storedValue(ENTITY_STORAGE_KEY),
      projectId: routed ? "" : storedValue(PROJECT_STORAGE_KEY),
      periodId: routed ? "" : storedValue(PERIOD_STORAGE_KEY),
    })
    const [companies, companyActions] = createResource(listCompanies)
    const [workspaceSettings, workspaceSettingsActions] = createResource(getWorkspaceSettings)
    const [projects, projectActions] = createResource(
      () => state.entityId || undefined,
      (entityID) => listProjects(entityID),
    )
    const selectedProject = createMemo(() =>
      state.entityId && projects.state === "ready"
        ? projects.latest?.find((project) => project.company_id === state.entityId && project.id === state.projectId)
        : undefined,
    )
    const [periods, periodActions] = createResource(
      selectedProject,
      (project) => listPeriods(project.id, project.company_id),
    )
    // .latest also throws on errors and can suspend before the first accepted value.
    const currentPeriods = createMemo(() =>
      periods.state === "ready" || periods.state === "refreshing"
        ? periods.latest?.filter((period) => period.company_id === state.entityId && period.project_id === state.projectId)
        : undefined,
    )
    const supportsPeriods = createMemo(() => {
      if (!state.projectId || !selectedProject()) return false
      const metadata = selectedProject()?.project_metadata
      return (
        (currentPeriods()?.length ?? 0) > 0 ||
        metadata?.supports_periods === true ||
        metadata?.period_driven === true ||
        metadata?.accounting_enabled === true
      )
    })

    createEffect(() => {
      const agentID = workspaceSettings()?.default_agent_id
      if (!agentID) return
      try {
        localStorage.setItem("cowork.agent.defaultChatAgentId", agentID)
      } catch {
        // The server value remains authoritative when browser storage is unavailable.
      }
      window.dispatchEvent(new CustomEvent("cowork:default-agent-changed", { detail: { agentID } }))
    })

    createEffect(() => {
      const value = workspaceSettings()?.response_language
      if (value) rememberCoworkResponseLanguage(activeCompanyID(), value)
    })

    createEffect(() => {
      const list = companies()
      if (!list || !state.entityId) return
      if (list.some((company) => company.id === state.entityId)) return
      setState("entityId", "")
      persistValue(ENTITY_STORAGE_KEY, "")
    })

    createEffect(() => {
      if (!state.entityId || projects.state !== "ready" || !state.projectId) return
      if (selectedProject()) return
      setState({ projectId: "", periodId: "" })
      persistValue(PROJECT_STORAGE_KEY, "")
      persistValue(PERIOD_STORAGE_KEY, "")
    })

    createEffect(() => {
      if (!selectedProject() || periods.state !== "ready") return
      const list = currentPeriods()
      if (!supportsPeriods()) {
        if (state.periodId === ALL_PERIODS) return
        if (!state.periodId) return
        setState("periodId", "")
        persistValue(PERIOD_STORAGE_KEY, "")
        return
      }
      if (!list?.length) return
      if (state.periodId === ALL_PERIODS || list.some((period) => period.id === state.periodId)) return
      const next =
        list.find((period) => period.is_current)?.id ??
        selectedProject()?.default_period_id ??
        list[0]?.id ??
        ALL_PERIODS
      setState("periodId", next)
      persistValue(PERIOD_STORAGE_KEY, next)
    })

    const selectedEntity = createMemo(() => companies()?.find((company) => company.id === state.entityId))
    const selectedPeriod = createMemo(() => currentPeriods()?.find((period) => period.id === state.periodId))
    const workspaceName = createMemo(() => workspaceSettings()?.workspace_name || "My workspace")

    const selectEntity = (entityId: string) => {
      const next = entityId
      if (next && next !== ALL_ENTITIES && next !== activeCompanyID()) setActiveCompanyID(next)
      if (next === state.entityId) return
      setState({ entityId: next, projectId: "", periodId: "" })
      persistValue(ENTITY_STORAGE_KEY, next)
      persistValue(PROJECT_STORAGE_KEY, "")
      persistValue(PERIOD_STORAGE_KEY, "")
    }

    const selectProject = (projectId: string) => {
      if (projectId === state.projectId) return
      setState({ projectId, periodId: "" })
      persistValue(PROJECT_STORAGE_KEY, projectId)
      persistValue(PERIOD_STORAGE_KEY, "")
    }

    const selectPeriod = (periodId: string) => {
      const next = periodId || ALL_PERIODS
      setState("periodId", next)
      persistValue(PERIOD_STORAGE_KEY, next)
    }

    return {
      state,
      allEntitiesID: ALL_ENTITIES,
      allPeriodsID: ALL_PERIODS,
      companies,
      projects,
      periods,
      selectedEntity,
      selectedProject,
      selectedPeriod,
      workspaceName,
      supportsPeriods,
      selectEntity,
      selectProject,
      selectPeriod,
      refreshPeriods: periodActions.refetch,
      refreshWorkspaceSettings: workspaceSettingsActions.refetch,
      refresh: () => Promise.all([companyActions.refetch(), projectActions.refetch(), periodActions.refetch()]),
    }
  },
})
